import React, { useState, ChangeEvent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyboardArrowLeft } from '@mui/icons-material';
import { useTheme, useMediaQuery } from '@mui/material';
import { ConfirmationModal, Error } from '~/Components/';
import { EventPageFloatingPanel } from './EventPageFloatingPanel/EventPageFloatingPanel';
import { EventPageInformation } from './EventPageInformation/EventPageInformation';
import { BookingList } from './BookingList';
import { EventPageStyles } from './EventPage.styles';
import { EventPageInformationDesktop } from './EventPageInformationDesktop';
import { useEventPage } from './useEventPage';

import { Route } from '~/Constants/Routes.constants';
import { useEventPageErrors } from './useEventPageErrors';
import { PrivateLayout } from '~/Layout/PrivateLayout';
import { Checkbox, FormControlLabel, TextareaAutosize, Typography } from '~/UIKit';
import { AccessList, useHasAccess } from '~/Hooks';
import { IconTooltip } from '~/Components/IconTooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { isIntegratedEvent } from '~/Helpers/Utils.helpers';

export const EventPage = () => {
  const { classes } = EventPageStyles();
  const { t } = useTranslation();
  const [sendMail, setSendMail] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const containerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { hasAccess } = useHasAccess();

  const handleCancelReasonChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setCancelReason(event.currentTarget.value);
  };

  const handleCancelConfirm = () => {
    modalConfirmHandler.cancel(sendMail, cancelReason);
  };

  const {
    selectedEvent,
    title,
    options,
    modalsOpen,
    modalConfirmHandler,
    handleOpenUncancel,
    handleOpenDelete,
    handleOpenCancel,
  } = useEventPage();

  const { singleEventErrors, goBack } = useEventPageErrors();

  if (!selectedEvent || singleEventErrors) {
    return singleEventErrors ? (
      <PrivateLayout backRoute={Route.PROVIDER_DETAIL} title={title} options={options}>
        <Error error={singleEventErrors} errorAction={goBack} ErrorIcon={KeyboardArrowLeft} />
      </PrivateLayout>
    ) : null;
  }

  const cancelModal = (
    <div className={classes.content}>
      <Typography className={classes.info}>{t('app.event.cancelEvent.continue')}</Typography>
      {hasAccess(AccessList.SEND_EMAIL_ON_EVENT_CANCEL) && (
        <div className={classes.wrapper}>
          <FormControlLabel
            className={classes.sendMail}
            control={
              <Checkbox
                checked={sendMail}
                color="primary"
                onChange={() => setSendMail(!sendMail)}
              />
            }
            label={t('app.event.cancelEvent.emailParticipants')}
          />
          <TextareaAutosize
            aria-label="Cancel reason"
            className={classes.cancelReason}
            disabled={!sendMail}
            onChange={handleCancelReasonChange}
            placeholder={t('app.event.cancelEvent.reason')}
            value={cancelReason}
          />
        </div>
      )}
    </div>
  );

  const source =
    !!selectedEvent?.source &&
    `${selectedEvent?.source}`.toLocaleLowerCase() !== 'fitogram' &&
    selectedEvent?.source;

  const isExternalEvent = isIntegratedEvent(selectedEvent);

  const headerComponent = (
    <>
      <div className={classes.integration}>{t('app.checkin.title.integration', { source })}</div>
      <IconTooltip
        tooltipIcon={<InfoOutlinedIcon className={classes.infoIcon} />}
        placement={'top-end'}
        container={containerRef?.current}
        popperOptions={{
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                boundary: 'window',
              },
            },
          ],
        }}
      >
        <Typography className={classes.itemCreationTooltipTitle} variant="body2">
          {t('app.checkin.title.integration.tooltip', { source })}
        </Typography>
      </IconTooltip>
    </>
  );

  const headerComponentContainer =
    !!source && !isMobile ? (
      <div className={classes.headerWrapper}>
        <div className={classes.titleWrapper}>
          <Typography className={classes.title} variant="h1">
            {`${title} |`}
          </Typography>
          {headerComponent}
        </div>
      </div>
    ) : undefined;

  const additionalInfo = !!source && isMobile ? headerComponent : undefined;

  return (
    <PrivateLayout
      title={!source ? title : undefined}
      backRoute={Route.PROVIDER_DETAIL}
      secondaryTitleActionElement={headerComponentContainer}
      // Do not allow modifications to events that were created via integration
      {...(!isExternalEvent && { options })}
    >
      <div className={classes.eventPageContainer}>
        <div className={classes.eventPageInformationSection}>
          <EventPageInformation additionalInfo={additionalInfo} />
          <EventPageFloatingPanel />
          <EventPageInformationDesktop />
        </div>
        <div className={classes.eventPageBookingList}>
          <BookingList />
        </div>
      </div>
      <div>
        <ConfirmationModal
          title={t('app.event.cancelEvent.title')}
          cancel={{ action: () => handleOpenCancel(false) }}
          confirm={{
            action: handleCancelConfirm,
            title: t('app.confirmationModal.cancel'),
          }}
          open={modalsOpen.cancel}
          destructive
        >
          {cancelModal}
        </ConfirmationModal>
        <ConfirmationModal
          title={t('app.event.uncancelEvent.title')}
          cancel={{ action: () => handleOpenUncancel(false) }}
          confirm={{
            action: modalConfirmHandler.uncancel,
            title: t('app.confirmationModal.confirm'),
          }}
          open={modalsOpen.uncancel}
        >
          <Typography className={classes.info}>{t('app.event.uncancelEvent.continue')}</Typography>
        </ConfirmationModal>
        <ConfirmationModal
          title={t('event.delete')}
          cancel={{ action: () => handleOpenDelete(false) }}
          confirm={{ action: modalConfirmHandler.delete, title: t('app.confirmationModal.delete') }}
          open={modalsOpen.delete}
          destructive
        >
          <Typography>{t('event.delete.confirmation')}</Typography>
        </ConfirmationModal>
      </div>
    </PrivateLayout>
  );
};
